import Button from '@atoms/Button/Button';
import CloseButton from '@atoms/CloseButton/CloseButton';
import Icon from '@atoms/Icon/Icon';
import { IconName } from '@models/IIcon';
import { ILink } from '@models/ILink';
import BannerNotificationStylesEnum from '@models/enums/BannerNotificationStylesEnum';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';
import { ReactNode, useState } from 'react';

type INotificationStyles = {
  readonly [key in BannerNotificationStylesEnum]: {
    readonly borderClassName?: string;
    readonly backgroundClassName?: string;
    readonly paddingClassName?: string;
    readonly textColorClassName?: string;
  };
};

interface Props {
  readonly displayStyle?: BannerNotificationStylesEnum;
  readonly additionalCustomStyles?: Partial<INotificationStyles>;
  readonly iconName?: IconName;
  readonly caption?: string;
  readonly closeLabel?: string;
  readonly title?: string | ReactNode;
  readonly className?: string;
  readonly contentClassName?: string;
  readonly link?: ILink;
  readonly primaryLink?: ILink;
  readonly secondaryLink?: ILink;
  readonly richText?: string;
  readonly additionalContent?: ReactNode;
  readonly desktopBreakpoint?: BreakpointEnum.Large | BreakpointEnum.XLarge;
  readonly isContentExpandable?: boolean;
  readonly showMoreLabel?: string;
  readonly showLessLabel?: string;
  readonly isLarge?: boolean;
  readonly dataTestId?: string;
  readonly onPrimaryLinkClick?: () => void;
  readonly onDismissNotification?: () => void;
}

const getNotificationStyles = (
  isLarge?: boolean,
  additionalCustomStyles?: Partial<INotificationStyles>
): INotificationStyles => {
  const paddingWithoutBorder = isLarge ? 'px-5 py-5 lg:py-7.5' : 'p-5';
  const paddingWithBorder = isLarge
    ? 'px-[1.1875rem] py-[1.1875rem] lg:py-[1.8125rem]'
    : 'p-[1.1875rem]';

  return {
    [BannerNotificationStylesEnum.Promo]: {
      backgroundClassName: 'bg-yellow',
      paddingClassName: paddingWithoutBorder,
      ...additionalCustomStyles?.[BannerNotificationStylesEnum.Promo],
    },
    [BannerNotificationStylesEnum.Info]: {
      backgroundClassName: 'bg-grey-100',
      paddingClassName: paddingWithoutBorder,
      ...additionalCustomStyles?.[BannerNotificationStylesEnum.Info],
    },
    [BannerNotificationStylesEnum.Warning]: {
      borderClassName: 'border border-yellow',
      paddingClassName: paddingWithBorder,
      backgroundClassName: 'bg-yellow-200',
      ...additionalCustomStyles?.[BannerNotificationStylesEnum.Warning],
    },
    [BannerNotificationStylesEnum.Error]: {
      borderClassName: 'border border-red-100',
      paddingClassName: paddingWithBorder,
      textColorClassName: 'text-red-100',
      ...additionalCustomStyles?.[BannerNotificationStylesEnum.Error],
    },
    [BannerNotificationStylesEnum.Success]: {
      borderClassName: 'border border-green-100',
      paddingClassName: paddingWithBorder,
      textColorClassName: 'text-green-100',
      ...additionalCustomStyles?.[BannerNotificationStylesEnum.Success],
    },
  };
};

const BannerNotification = ({
  displayStyle,
  additionalCustomStyles,
  iconName,
  caption,
  closeLabel,
  title,
  className,
  contentClassName,
  link,
  primaryLink,
  secondaryLink,
  richText,
  additionalContent,
  desktopBreakpoint = BreakpointEnum.Large,
  isContentExpandable,
  showMoreLabel,
  showLessLabel,
  isLarge,
  dataTestId = 'Banner-Notification',
  onPrimaryLinkClick,
  onDismissNotification,
}: Props) => {
  const [richTextRef, { scrollHeight: richTextContentHeight }] =
    useResizeObserver();
  const [isRichTextExpanded, setIsRichTextExpanded] = useState(false);
  const isMobile = useMediaQuery(
    desktopBreakpoint === BreakpointEnum.Large ? 1024 : 1440
  );
  const richTextCutOff = isMobile ? 72 : 24;
  const isRichTextExpandable = richTextContentHeight > richTextCutOff;
  const {
    borderClassName,
    backgroundClassName,
    textColorClassName = 'text-black-100',
    paddingClassName,
  } = getNotificationStyles(isLarge, additionalCustomStyles)[
    displayStyle || BannerNotificationStylesEnum.Info
  ];

  return (
    <div
      className={classNames(
        'flex w-full gap-x-5 rounded-[10px]',
        borderClassName,
        backgroundClassName,
        textColorClassName,
        paddingClassName,
        className
      )}
      data-testid={dataTestId}
      aria-live="polite"
    >
      {iconName && (
        <div
          className={classNames('flex', {
            'lg:items-center': primaryLink || secondaryLink,
          })}
          data-testid={`${dataTestId}-Icon`}
        >
          <Icon name={iconName} colorClassName={textColorClassName} />
        </div>
      )}

      <div
        className={classNames(
          'flex w-full flex-col justify-between',
          desktopBreakpoint === BreakpointEnum.Large
            ? 'max-lg:gap-y-2.5 lg:flex-row lg:gap-x-7.5'
            : 'max-xl:gap-y-2.5 xl:flex-row xl:gap-x-7.5',
          {
            'lg:items-center': closeLabel || primaryLink || secondaryLink,
          },
          contentClassName
        )}
      >
        {(caption || title) && !richText && (
          <div>
            {caption && (
              <span
                className="font-normal"
                data-testid={`${dataTestId}-Caption`}
              >
                {caption}
              </span>
            )}{' '}
            {title}
          </div>
        )}

        {richText && !isContentExpandable && (
          <div
            className="body-three [&_a]:underline"
            data-testid={`${dataTestId}-Rich-Text`}
            dangerouslySetInnerHTML={{ __html: richText }}
          />
        )}

        {richText && isContentExpandable && (
          <div
            className={classNames(
              'flex items-start',
              desktopBreakpoint === BreakpointEnum.Large
                ? 'max-lg:flex-col'
                : 'max-xl:flex-col'
            )}
          >
            <motion.div
              ref={richTextRef}
              className="body-three overflow-hidden [&_a]:underline"
              animate={!isRichTextExpanded ? 'initial' : 'animate'}
              variants={{
                animate: { height: 'auto' },
                initial: {
                  height: isRichTextExpandable ? richTextCutOff : 'auto',
                },
              }}
              dangerouslySetInnerHTML={{ __html: richText }}
              data-testid={`${dataTestId}-Rich-Text`}
            />

            {isRichTextExpandable && (
              <button
                className="shrink-0 underline"
                onClick={() => {
                  setIsRichTextExpanded(!isRichTextExpanded);
                }}
                data-testid={`${dataTestId}-Show-More/Less`}
              >
                <strong>
                  {isRichTextExpanded ? showLessLabel : showMoreLabel}
                </strong>
              </button>
            )}
          </div>
        )}

        {link?.url && link?.name && (
          <div
            className={classNames(
              'mr-2.5 flex shrink-0 flex-row',
              desktopBreakpoint === BreakpointEnum.Large
                ? 'lg:flex-col'
                : 'xl:flex-col'
            )}
          >
            <a
              href={link.url}
              target={link.target}
              className="font-normal underline"
              data-testid={`${dataTestId}-Link`}
            >
              {link?.name}
            </a>
          </div>
        )}

        {(primaryLink?.url || secondaryLink?.url) && (
          <div className={classNames('flex flex-row gap-5')}>
            {primaryLink?.url && primaryLink?.name && (
              <Button
                link={primaryLink}
                data-testid={`${dataTestId}-Primary-Link`}
                className="bg-black px-4 py-1 text-white hover:bg-grey-250 focus:bg-grey-250 active:bg-grey-250"
                hasCustomStyling
                linkCallback={onPrimaryLinkClick}
              >
                {primaryLink.name}
              </Button>
            )}

            {secondaryLink?.url && secondaryLink?.name && (
              <Button
                link={secondaryLink}
                data-testid={`${dataTestId}-Secondary-Link`}
                className="border border-black bg-transparent px-[15px] py-[3px] text-black hover:bg-black hover:text-white focus:bg-black focus:text-white active:bg-black active:text-white"
                hasCustomStyling
              >
                {secondaryLink.name}
              </Button>
            )}
          </div>
        )}

        {additionalContent}

        {closeLabel && (
          <FocusTrap focusTrapOptions={{ initialFocus: false }}>
            <div>
              {isMobile ? (
                <button
                  className="body-three body-three--bold text-start underline"
                  onClick={() => {
                    onDismissNotification?.();
                  }}
                >
                  {closeLabel}
                </button>
              ) : (
                <CloseButton
                  className="relative"
                  color="red"
                  size={IconSizeEnum.md}
                  onClick={() => {
                    onDismissNotification?.();
                  }}
                />
              )}
            </div>
          </FocusTrap>
        )}
      </div>
    </div>
  );
};

export default BannerNotification;
